import { suggestNextAccountCodeText } from "@helpers/suggestNextAccountCode";
import { AllocateCodeText, DraftAccount, DraftGroup } from "../draftOperations";
import { EditorCodeRules } from "../useEditorDraftValidation";
import { NestedEditorRow } from "../nested/nestedRowTypes";
import { buildMergedNestedRows } from "../nested/buildMergedNestedRows";

// Blank codeText is never a duplicate (Postgres allows multiple nulls).
// Non-null codes collide per business.
export const findWorkspaceDuplicateCodeIds = (
    accounts: DraftAccount[],
): Set<string> => {
    const idsByScope = new Map<string, string[]>();
    for (const account of accounts) {
        const codeText = account.codeText?.trim() ?? "";
        if (codeText.length === 0) {
            continue;
        }
        const scopeKey = `${account.entityId}::${codeText}`;
        const existing = idsByScope.get(scopeKey);
        if (existing) {
            existing.push(account.id);
        } else {
            idsByScope.set(scopeKey, [account.id]);
        }
    }
    const duplicates = new Set<string>();
    for (const ids of idsByScope.values()) {
        if (ids.length > 1) {
            ids.forEach((id) => duplicates.add(id));
        }
    }
    return duplicates;
};

// Live-chart codes are free text: suggest on create, never rewrite on relocate.
export const workspaceAllocateCodeText: AllocateCodeText = (params) => {
    if (params.kind === "relocate") {
        return params.currentCodeText;
    }
    return suggestNextAccountCodeText({
        accounts: params.accounts,
        targets: params.targets,
        type: params.type,
    });
};

export const workspaceEditorCodeRules: EditorCodeRules = {
    findDuplicateCodeIds: findWorkspaceDuplicateCodeIds,
};

export const makeWorkspaceBuildRows =
    (displayedEntityIds: number[]) =>
    ({
        accounts,
        groups,
        pendingGestureIdByDraftId,
        hasActiveFilter,
    }: {
        accounts: DraftAccount[];
        groups: DraftGroup[];
        pendingGestureIdByDraftId?: ReadonlyMap<string, string>;
        hasActiveFilter?: boolean;
    }): NestedEditorRow[] =>
        buildMergedNestedRows({
            accounts,
            groups,
            displayedEntityIds,
            pendingGestureIdByDraftId,
            hasActiveFilter,
        });
